import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import {
  BellRing,
  CalendarDays,
  Check,
  CheckCheck,
  Clock3,
  Eye,
  FileText,
  HandCoins,
  Mail,
  Phone,
  PhoneCall,
  Save,
  Search,
  SquarePen,
  TicketX,
  WandSparkles,
} from 'lucide-react';
import { toast } from 'react-toastify';
import scadenzarioApi from '../services/scadenzarioApi';

const RATE_VISUAL_META = {
  scaduta: { label: 'Scaduta', color: '#DC2626', bg: 'rgba(220, 38, 38, 0.12)' },
  in_scadenza: { label: 'In scadenza', color: '#CA8A04', bg: 'rgba(202, 138, 4, 0.14)' },
  pagata: { label: 'Pagata', color: '#16A34A', bg: 'rgba(22, 163, 74, 0.12)' },
  futura: { label: 'Futura', color: '#9CA3AF', bg: 'rgba(156, 163, 175, 0.2)' },
};

const RATE_STATE_META = {
  pendenza: { label: 'Pendente', color: '#475569', bg: 'rgba(100, 116, 139, 0.12)' },
  pendente: { label: 'Pendente', color: '#475569', bg: 'rgba(100, 116, 139, 0.12)' },
  parzialmente_pagata: {
    label: 'Parz. pagata',
    color: '#CA8A04',
    bg: 'rgba(202, 138, 4, 0.14)',
  },
  scaduta: { label: 'Scaduta', color: '#DC2626', bg: 'rgba(220, 38, 38, 0.12)' },
  pagata: { label: 'Pagata', color: '#16A34A', bg: 'rgba(22, 163, 74, 0.12)' },
  annullata: { label: 'Annullata', color: '#64748B', bg: 'rgba(148, 163, 184, 0.18)' },
};

const PROMESSA_META = {
  aperta: { label: 'Aperta', color: '#2563EB', bg: 'rgba(37, 99, 235, 0.12)' },
  mantenuta: { label: 'Mantenuta', color: '#16A34A', bg: 'rgba(22, 163, 74, 0.12)' },
  mancata: { label: 'Mancata', color: '#DC2626', bg: 'rgba(220, 38, 38, 0.12)' },
  annullata: { label: 'Annullata', color: '#6B7280', bg: 'rgba(107, 114, 128, 0.18)' },
  scaduta: { label: 'Scaduta', color: '#CA8A04', bg: 'rgba(202, 138, 4, 0.14)' },
};

const getPromessaMeta = (stato) =>
  PROMESSA_META[String(stato || '').toLowerCase()] || {
    label: String(stato || 'N/D'),
    color: '#6B7280',
    bg: 'rgba(107, 114, 128, 0.18)',
  };

const getApiErrorMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.errors?.[0]?.msg ||
  fallback;

const STORICO_EVENT_META = {
  pagamento_creato: {
    label: 'Pagamento registrato',
    color: '#3B82F6',
    bg: 'rgba(59, 130, 246, 0.12)',
    Icon: HandCoins,
  },
  pagamento_annullato: {
    label: 'Pagamento annullato',
    color: '#EF4444',
    bg: 'rgba(239, 68, 68, 0.12)',
    Icon: TicketX,
  },
  promessa_creata: {
    label: 'Promessa creata',
    color: '#8B5CF6',
    bg: 'rgba(139, 92, 246, 0.12)',
    Icon: FileText,
  },
  sollecito_creato: {
    label: 'Sollecito inviato',
    color: '#F59E0B',
    bg: 'rgba(245, 158, 11, 0.14)',
    Icon: PhoneCall,
  },
  stato_aggiornato: {
    label: 'Stato aggiornato',
    color: '#10B981',
    bg: 'rgba(16, 185, 129, 0.12)',
    Icon: CheckCheck,
  },
};

const getStoricoMeta = (eventoTipo) =>
  STORICO_EVENT_META[String(eventoTipo || '').toLowerCase()] || {
    label: String(eventoTipo || 'Evento'),
    color: '#64748B',
    bg: 'rgba(100, 116, 139, 0.12)',
    Icon: Clock3,
  };

const getRateVisualStatus = (row) => {
  const stato = String(row?.stato_rate || '').toLowerCase();
  const importoPagato = Number(row?.importo_pagato || 0);
  const importoPrevisto = Number(row?.importo_previsto || 0);
  const isPagata =
    stato === 'pagata' || (importoPrevisto > 0 && importoPagato >= importoPrevisto);
  if (isPagata) {
    return { key: 'pagata', diff: null };
  }

  if (!row?.data_scadenza) {
    return { key: 'futura', diff: null };
  }
  const due = new Date(row.data_scadenza);
  const today = new Date();
  due.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((due - today) / (1000 * 60 * 60 * 24));

  if (diff < 0) {
    return { key: 'scaduta', diff };
  }
  if (diff <= 7) {
    return { key: 'in_scadenza', diff };
  }
  return { key: 'futura', diff };
};

const getScadenzaLabel = (row) => {
  if (!row?.data_scadenza) return '-';
  const visual = getRateVisualStatus(row);
  const diff = visual.diff;
  if (visual.key === 'pagata') return 'Pagata';
  if (visual.key === 'scaduta') return `Scaduta da ${Math.abs(diff)} giorni`;
  if (visual.key === 'in_scadenza') {
    if (diff === 0) return 'Scade oggi';
    return `Entro ${diff} giorni`;
  }
  if (typeof diff === 'number') return `Tra ${diff} giorni`;
  return 'Futura';
};

const StyledCard = styled(Card)(() => ({
  boxShadow:
    '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
}));

const StatWidget = ({ title, value, icon: Icon, color }) => (
  <Paper
    sx={{
      p: 2,
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: 'rgba(145, 158, 171, 0.24)',
      boxShadow:
        '0 0 2px 0 rgba(145, 158, 171, 0.16), 0 12px 24px -4px rgba(145, 158, 171, 0.08)',
    }}
    elevation={0}
    variant="outlined"
  >
    <Box
      sx={{
        p: 1.5,
        borderRadius: '50%',
        bgcolor: `${color}15`,
        color,
        mr: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon size={24} />
    </Box>
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
        {title}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
        {value}
      </Typography>
    </Box>
  </Paper>
);

export default function ScadenzarioRate() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [scaduteVisualTotal, setScaduteVisualTotal] = useState(0);
  const [importoPrevistoTotale, setImportoPrevistoTotale] = useState(0);
  const [importoPagatoTotale, setImportoPagatoTotale] = useState(0);
  const [saldoResiduoTotale, setSaldoResiduoTotale] = useState(0);
  const [search, setSearch] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [statoRate, setStatoRate] = useState('pendente,parzialmente_pagata,scaduta');
  const [promessaFilter, setPromessaFilter] = useState('all');
  const [scadenzaView, setScadenzaView] = useState('scadute_fino_oggi');
  const [customFromDate, setCustomFromDate] = useState('');
  const [customToDate, setCustomToDate] = useState('');
  const [orderBy, setOrderBy] = useState('data_scadenza');
  const [orderDirection, setOrderDirection] = useState('asc');
  const [userId, setUserId] = useState('all');
  const [meta, setMeta] = useState({
    referenti: [],
    statiRate: [],
  });
  const referentiWithAll = useMemo(
    () => [{ id: 'all', name: 'Tutti' }, ...(meta.referenti || [])],
    [meta.referenti]
  );

  const [selectedIds, setSelectedIds] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedRata, setSelectedRata] = useState(null);
  const [worklistOpen, setWorklistOpen] = useState(false);
  const [worklistTab, setWorklistTab] = useState(0);
  const [worklistLoading, setWorklistLoading] = useState(false);
  const [worklistPagamenti, setWorklistPagamenti] = useState([]);
  const [worklistSolleciti, setWorklistSolleciti] = useState([]);
  const [worklistPromesse, setWorklistPromesse] = useState([]);
  const [worklistStorico, setWorklistStorico] = useState([]);
  const [promessaEdits, setPromessaEdits] = useState({});
  const [newPagamentoForm, setNewPagamentoForm] = useState({
    importo: '',
    data_pagamento: '',
    metodo_pagamento: 'bonifico',
    note: '',
  });
  const [newSollecitoForm, setNewSollecitoForm] = useState({
    template_id: '',
    canale: 'email',
    tipo_sollecito: 'promemoria',
    messaggio_renderizzato: '',
  });
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateSolleciti, setTemplateSolleciti] = useState([]);
  const [templateForm, setTemplateForm] = useState({
    id: null,
    codice: '',
    nome: '',
    canale: 'email',
    soggetto: '',
    corpo_template: '',
    variabili_json: '',
    attivo: true,
  });
  const [promemoriaConfirmOpen, setPromemoriaConfirmOpen] = useState(false);
  const [promemoriaDialogOpen, setPromemoriaDialogOpen] = useState(false);
  const [promemoriaSending, setPromemoriaSending] = useState(false);
  const [promemoriaMode, setPromemoriaMode] = useState('simulato');
  const [promemoriaTemplateId, setPromemoriaTemplateId] = useState('');
  const [promemoriaAccountId, setPromemoriaAccountId] = useState('');
  const [promemoriaEmailAccounts, setPromemoriaEmailAccounts] = useState([]);
  const [promemoriaEmailAccountsLoading, setPromemoriaEmailAccountsLoading] =
    useState(false);
  const [newPromessaForm, setNewPromessaForm] = useState({
    data_promessa: '',
    importo_promesso: '',
    note: '',
  });
  const [summaryPulse, setSummaryPulse] = useState(false);
  const fetchRateRequestRef = useRef(0);
  const lastSummarySignatureRef = useRef('');
  const importoPrevistoSelezionato = Number(selectedRata?.importo_previsto || 0);
  const importoPagatoSelezionato = useMemo(() => {
    const totaleDaMovimenti = (worklistPagamenti || []).reduce((acc, item) => {
      if (Number(item?.annullato) === 1) return acc;
      return acc + Number(item?.importo || 0);
    }, 0);
    if (worklistOpen) {
      return Number(totaleDaMovimenti.toFixed(2));
    }
    return Number(selectedRata?.importo_pagato_totale ?? selectedRata?.importo_pagato ?? 0);
  }, [selectedRata?.importo_pagato, selectedRata?.importo_pagato_totale, worklistOpen, worklistPagamenti]);
  const importoResiduoSelezionato = useMemo(() => {
    if (worklistOpen) {
      return Math.max(0, Number(importoPrevistoSelezionato - importoPagatoSelezionato));
    }
    return Math.max(
      0,
      Number(
        selectedRata?.importo_residuo ??
          importoPrevistoSelezionato - importoPagatoSelezionato
      )
    );
  }, [
    importoPagatoSelezionato,
    importoPrevistoSelezionato,
    selectedRata?.importo_residuo,
    worklistOpen,
  ]);

  useEffect(() => {
    if (!worklistOpen) return;
    const signature = `${importoPrevistoSelezionato}|${importoPagatoSelezionato}|${importoResiduoSelezionato}`;
    if (
      lastSummarySignatureRef.current &&
      lastSummarySignatureRef.current !== signature
    ) {
      setSummaryPulse(true);
      const timeout = setTimeout(() => setSummaryPulse(false), 520);
      lastSummarySignatureRef.current = signature;
      return () => clearTimeout(timeout);
    }
    lastSummarySignatureRef.current = signature;
    return undefined;
  }, [
    importoPagatoSelezionato,
    importoPrevistoSelezionato,
    importoResiduoSelezionato,
    worklistOpen,
  ]);

  const fetchMeta = useCallback(async () => {
    try {
      const res = await scadenzarioApi.getMeta();
      setMeta({
        referenti: res.data?.referenti || [],
        statiRate: res.data?.statiRate || [],
      });
    } catch (error) {
      toast.error('Errore nel caricamento metadati rate');
    }
  }, []);

  const fetchRate = useCallback(async () => {
    const requestId = fetchRateRequestRef.current + 1;
    fetchRateRequestRef.current = requestId;
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        stato_rate: statoRate,
        scadenza_view: scadenzaView,
        orderBy,
        orderDirection,
      };
      if (
        scadenzaView === 'intervallo_personalizzato' &&
        customFromDate &&
        customToDate
      ) {
        const fromDate = customFromDate <= customToDate ? customFromDate : customToDate;
        const toDate = customFromDate <= customToDate ? customToDate : customFromDate;
        params.from_date = fromDate;
        params.to_date = toDate;
      }
      if (promessaFilter === 'with') params.has_promessa_attiva = true;
      if (promessaFilter === 'without') params.has_promessa_attiva = false;
      if (searchValue) params.search = searchValue;
      if (userId !== 'all') params.user_id = userId;
      if (searchValue) {
        params.scadenza_view = 'tutte';
        params.stato_rate = 'pendente,parzialmente_pagata,scaduta,pagata,annullata';
      }

      const res = await scadenzarioApi.getRate(params);
      if (requestId !== fetchRateRequestRef.current) return;
      const nextRows = res.data?.data || [];
      setRows(nextRows);
      setTotal(res.data?.total || 0);
      const previstoTotaleServer = Number(res.data?.importoPrevistoTotale);
      const pagatoTotaleServer = Number(res.data?.importoPagatoTotale);
      const residuoTotaleServer = Number(res.data?.saldoResiduoTotale);
      const previstoTotaleFallback = nextRows.reduce(
        (sum, row) => sum + Number(row.importo_previsto || 0),
        0
      );
      const pagatoTotaleFallback = nextRows.reduce(
        (sum, row) => sum + Number(row.importo_pagato || 0),
        0
      );
      setImportoPrevistoTotale(
        Number.isFinite(previstoTotaleServer) ? previstoTotaleServer : previstoTotaleFallback
      );
      setImportoPagatoTotale(
        Number.isFinite(pagatoTotaleServer) ? pagatoTotaleServer : pagatoTotaleFallback
      );
      setSaldoResiduoTotale(
        Number.isFinite(residuoTotaleServer)
          ? residuoTotaleServer
          : Math.max(0, previstoTotaleFallback - pagatoTotaleFallback)
      );
      setScaduteVisualTotal(
        Number(
          res.data?.scaduteVisualTotal ??
            nextRows.filter((row) => getRateVisualStatus(row).key === 'scaduta').length
        )
      );
    } catch (error) {
      if (requestId !== fetchRateRequestRef.current) return;
      toast.error(error?.response?.data?.message || 'Errore nel caricamento rate');
    } finally {
      if (requestId !== fetchRateRequestRef.current) return;
      setLoading(false);
    }
  }, [
    customFromDate,
    customToDate,
    page,
    orderBy,
    orderDirection,
    promessaFilter,
    rowsPerPage,
    scadenzaView,
    searchValue,
    statoRate,
    userId,
  ]);

  useEffect(() => {
    fetchMeta();
  }, [fetchMeta]);

  useEffect(() => {
    fetchRate();
  }, [fetchRate]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(0);
      setSearchValue(search.trim().length >= 1 ? search.trim() : '');
    }, 350);
    return () => clearTimeout(handler);
  }, [search]);

  const rateScaduteCount = useMemo(
    () => Number(scaduteVisualTotal || 0),
    [scaduteVisualTotal]
  );

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(rows.map((row) => row.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSendPromemoria = () => {
    if (!selectedIds.length) {
      toast.info('Seleziona almeno una rata');
      return;
    }
    setPromemoriaConfirmOpen(true);
  };

  const handleProceedPromemoria = () => {
    setPromemoriaConfirmOpen(false);
    setPromemoriaDialogOpen(true);
    loadTemplateSolleciti();
    loadPromemoriaEmailAccounts();
  };

  const handleConfirmSendPromemoria = async () => {
    if (!selectedIds.length) {
      toast.info('Seleziona almeno una rata');
      return;
    }
    if (promemoriaMode === 'reale_email' && !promemoriaEmailAccounts.length) {
      toast.error('Nessun account email attivo disponibile');
      return;
    }
    if (
      (promemoriaMode === 'reale_email' || promemoriaMode === 'reale_whatsapp') &&
      !templatePromemoriaSolleciti.length
    ) {
      toast.error('Nessun template disponibile per il canale selezionato');
      return;
    }
    try {
      setPromemoriaSending(true);
      const payload = {
        ids: selectedIds,
        mode: promemoriaMode,
      };
      if (
        (promemoriaMode === 'reale_email' || promemoriaMode === 'reale_whatsapp') &&
        promemoriaTemplateId
      ) {
        payload.template_id = Number(promemoriaTemplateId);
      }
      if (promemoriaMode === 'reale_email' && promemoriaAccountId) {
        payload.account_id = Number(promemoriaAccountId);
      }
      const res = await scadenzarioApi.inviaPromemoria(payload);
      toast.success(res.data?.message || 'Promemoria inviati');
      setSelectedIds([]);
      setPromemoriaDialogOpen(false);
      fetchRate();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Errore invio promemoria');
    } finally {
      setPromemoriaSending(false);
    }
  };

  const loadWorklistData = useCallback(async (rataId) => {
    setWorklistLoading(true);
    try {
      const [pagamentiRes, sollecitiRes, promesseRes, storicoRes] = await Promise.allSettled([
        scadenzarioApi.listPagamentiRata(rataId),
        scadenzarioApi.listSollecitiRata(rataId),
        scadenzarioApi.listPromesseRata(rataId),
        scadenzarioApi.listStoricoRata(rataId),
      ]);
      const pagamentiData =
        pagamentiRes.status === 'fulfilled'
          ? pagamentiRes.value.data?.data || []
          : [];
      const sollecitiData =
        sollecitiRes.status === 'fulfilled'
          ? sollecitiRes.value.data?.data || []
          : [];
      const promesseData =
        promesseRes.status === 'fulfilled'
          ? promesseRes.value.data?.data || []
          : [];
      const storicoData =
        storicoRes.status === 'fulfilled' ? storicoRes.value.data?.data || [] : [];
      setWorklistPagamenti(pagamentiData);
      setWorklistSolleciti(sollecitiData);
      setWorklistPromesse(promesseData);
      setWorklistStorico(storicoData);
      setPromessaEdits(
        promesseData.reduce((acc, item) => {
          acc[item.id] = {
            stato_promessa: item.stato_promessa || 'aperta',
            note: item.note || '',
          };
          return acc;
        }, {})
      );
    } finally {
      setWorklistLoading(false);
    }
  }, []);

  const openWorklistDialog = (row) => {
    setSelectedRata(row);
    setWorklistTab(0);
    setNewPagamentoForm({
      importo: '',
      data_pagamento: '',
      metodo_pagamento: 'bonifico',
      note: '',
    });
    setNewSollecitoForm({
      template_id: '',
      canale: 'email',
      tipo_sollecito: 'promemoria',
      messaggio_renderizzato: '',
    });
    setNewPromessaForm({
      data_promessa: '',
      importo_promesso: '',
      note: '',
    });
    setWorklistOpen(true);
    loadWorklistData(row.id);
    loadTemplateSolleciti();
  };

  const handleCreatePagamento = async () => {
    if (!selectedRata) return;
    const importo = Number(newPagamentoForm.importo || 0);
    if (!importo || importo <= 0) {
      toast.error('Inserisci un importo pagamento valido');
      return;
    }
    if (importo > importoResiduoSelezionato + 0.009) {
      toast.error(
        `Importo superiore al residuo (€ ${importoResiduoSelezionato.toLocaleString('it-IT', {
          minimumFractionDigits: 2,
        })})`
      );
      return;
    }
    try {
      await scadenzarioApi.createPagamentoRata(selectedRata.id, {
        importo,
        data_pagamento: newPagamentoForm.data_pagamento || null,
        metodo_pagamento: newPagamentoForm.metodo_pagamento,
        note: newPagamentoForm.note || null,
      });
      toast.success('Pagamento registrato');
      setNewPagamentoForm((prev) => ({ ...prev, importo: '', note: '' }));
      loadWorklistData(selectedRata.id);
      fetchRate();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Errore registrazione pagamento'));
    }
  };

  const handleCreateSollecito = async () => {
    if (!selectedRata) return;
    try {
      await scadenzarioApi.createSollecitoRata(selectedRata.id, {
        template_id: newSollecitoForm.template_id
          ? Number(newSollecitoForm.template_id)
          : null,
        canale: newSollecitoForm.canale,
        tipo_sollecito: newSollecitoForm.tipo_sollecito,
        messaggio_renderizzato: newSollecitoForm.messaggio_renderizzato || null,
      });
      toast.success('Sollecito registrato');
      setNewSollecitoForm((prev) => ({
        ...prev,
        template_id: '',
        messaggio_renderizzato: '',
      }));
      loadWorklistData(selectedRata.id);
      fetchRate();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Errore creazione sollecito'));
    }
  };

  const loadTemplateSolleciti = useCallback(async () => {
    setTemplateLoading(true);
    try {
      const res = await scadenzarioApi.listTemplateSolleciti();
      const list = res.data?.data || [];
      if (list.length > 0) {
        setTemplateSolleciti(list);
        return;
      }
      const seedRes = await scadenzarioApi.seedTemplateSolleciti();
      setTemplateSolleciti(seedRes.data?.data || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Errore caricamento template'));
    } finally {
      setTemplateLoading(false);
    }
  }, []);

  const loadPromemoriaEmailAccounts = useCallback(async () => {
    setPromemoriaEmailAccountsLoading(true);
    try {
      const res = await scadenzarioApi.listEmailAccounts();
      const accounts = res.data?.data || [];
      setPromemoriaEmailAccounts(accounts);
      if (!promemoriaAccountId && accounts.length) {
        setPromemoriaAccountId(String(accounts[0].id));
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Errore caricamento account email'));
    } finally {
      setPromemoriaEmailAccountsLoading(false);
    }
  }, [promemoriaAccountId]);

  const handleSelectTemplate = (templateId) => {
    const selectedId = templateId ? Number(templateId) : null;
    const template = (templateSolleciti || []).find((t) => Number(t.id) === selectedId);
    setNewSollecitoForm((prev) => ({
      ...prev,
      template_id: templateId,
      canale: template?.canale || prev.canale,
      messaggio_renderizzato: template?.corpo_template || '',
    }));
  };

  const handleEditTemplate = (template) => {
    setTemplateForm({
      id: template?.id || null,
      codice: template?.codice || '',
      nome: template?.nome || '',
      canale: template?.canale || 'email',
      soggetto: template?.soggetto || '',
      corpo_template: template?.corpo_template || '',
      variabili_json: template?.variabili_json
        ? JSON.stringify(template.variabili_json, null, 2)
        : '',
      attivo: Number(template?.attivo ?? 1) === 1,
    });
    setTemplateDialogOpen(true);
  };

  const handlePickTemplateToEdit = (templateId) => {
    if (!templateId) {
      handleNewTemplate();
      return;
    }
    const selected = (templateSolleciti || []).find(
      (template) => Number(template.id) === Number(templateId)
    );
    if (!selected) return;
    handleEditTemplate(selected);
  };

  const handleNewTemplate = () => {
    setTemplateForm({
      id: null,
      codice: '',
      nome: '',
      canale: newSollecitoForm.canale || 'email',
      soggetto: '',
      corpo_template: '',
      variabili_json: '',
      attivo: true,
    });
    setTemplateDialogOpen(true);
  };

  const handleEditSelectedTemplate = () => {
    if (!newSollecitoForm.template_id) return;
    handlePickTemplateToEdit(newSollecitoForm.template_id);
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.codice || !templateForm.nome || !templateForm.corpo_template) {
      toast.error('Compila codice, nome e corpo template');
      return;
    }
    let variabiliJsonPayload = null;
    if (templateForm.variabili_json.trim()) {
      try {
        variabiliJsonPayload = JSON.parse(templateForm.variabili_json);
      } catch (_err) {
        toast.error('Variabili JSON non valide');
        return;
      }
    }
    const payload = {
      codice: templateForm.codice.trim(),
      nome: templateForm.nome.trim(),
      canale: templateForm.canale,
      soggetto: templateForm.soggetto.trim() || null,
      corpo_template: templateForm.corpo_template,
      variabili_json: variabiliJsonPayload,
      attivo: Boolean(templateForm.attivo),
    };
    try {
      if (templateForm.id) {
        await scadenzarioApi.updateTemplateSolleciti(templateForm.id, payload);
        toast.success('Template aggiornato');
      } else {
        await scadenzarioApi.createTemplateSolleciti(payload);
        toast.success('Template creato');
      }
      setTemplateDialogOpen(false);
      await loadTemplateSolleciti();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Errore salvataggio template'));
    }
  };

  const handleCreatePromessa = async () => {
    if (!selectedRata) return;
    const importoPromesso = Number(newPromessaForm.importo_promesso || 0);
    if (!newPromessaForm.data_promessa) {
      toast.error('Inserisci data promessa');
      return;
    }
    if (!importoPromesso || importoPromesso <= 0) {
      toast.error('Inserisci importo promesso valido');
      return;
    }
    try {
      await scadenzarioApi.createPromessaRata(selectedRata.id, {
        data_promessa: newPromessaForm.data_promessa,
        importo_promesso: importoPromesso,
        note: newPromessaForm.note || null,
      });
      toast.success('Promessa registrata');
      setNewPromessaForm((prev) => ({ ...prev, importo_promesso: '', note: '' }));
      loadWorklistData(selectedRata.id);
      fetchRate();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Errore creazione promessa'));
    }
  };

  const handleAnnullaPagamento = async (pagamentoId) => {
    if (!selectedRata) return;
    try {
      await scadenzarioApi.annullaPagamentoRata(pagamentoId, {});
      toast.success('Pagamento annullato');
      loadWorklistData(selectedRata.id);
      fetchRate();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Errore annullamento pagamento'));
    }
  };

  const handlePromessaFieldChange = (promessaId, field, value) => {
    setPromessaEdits((prev) => ({
      ...prev,
      [promessaId]: { ...(prev[promessaId] || {}), [field]: value },
    }));
  };

  const handleSavePromessa = async (promessaId) => {
    const patch = promessaEdits[promessaId];
    if (!patch) return;
    try {
      await scadenzarioApi.updatePromessaRata(promessaId, {
        stato_promessa: patch.stato_promessa,
        note: patch.note || null,
      });
      toast.success('Promessa aggiornata');
      if (selectedRata) {
        loadWorklistData(selectedRata.id);
        fetchRate();
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Errore aggiornamento promessa'));
    }
  };

  const allSelected = rows.length > 0 && selectedIds.length === rows.length;
  const sortableColumns = useMemo(
    () =>
      new Set([
        'piano_rateazione_id',
        'nr_rata',
        'data_scadenza',
        'importo_previsto',
        'importo_pagato',
        'stato_rate',
      ]),
    []
  );
  const handleSort = useCallback(
    (columnId) => {
      if (!sortableColumns.has(columnId)) return;
      const isSameColumn = orderBy === columnId;
      setOrderBy(columnId);
      setOrderDirection(isSameColumn && orderDirection === 'asc' ? 'desc' : 'asc');
      setPage(0);
    },
    [orderBy, orderDirection, sortableColumns]
  );
  const renderHeadCell = useCallback(
    (label, columnId) => {
      if (!columnId || !sortableColumns.has(columnId)) {
        return label;
      }
      const isActive = orderBy === columnId;
      return (
        <TableSortLabel
          active={isActive}
          direction={isActive ? orderDirection : 'asc'}
          onClick={() => handleSort(columnId)}
        >
          {label}
        </TableSortLabel>
      );
    },
    [handleSort, orderBy, orderDirection, sortableColumns]
  );
  const filteredTemplateSolleciti = useMemo(
    () =>
      (templateSolleciti || []).filter(
        (t) => Number(t.attivo) === 1 && t.canale === newSollecitoForm.canale
      ),
    [newSollecitoForm.canale, templateSolleciti]
  );
  const templatePlaceholdersFromBody = useMemo(() => {
    const text = String(templateForm.corpo_template || '');
    const matches = [...text.matchAll(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g)];
    return Array.from(new Set(matches.map((m) => m[1])));
  }, [templateForm.corpo_template]);
  const templateJsonKeys = useMemo(() => {
    const raw = String(templateForm.variabili_json || '').trim();
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return [];
      return Object.keys(parsed);
    } catch (_e) {
      return [];
    }
  }, [templateForm.variabili_json]);
  const templateJsonInvalid = useMemo(() => {
    const raw = String(templateForm.variabili_json || '').trim();
    if (!raw) return false;
    try {
      JSON.parse(raw);
      return false;
    } catch (_e) {
      return true;
    }
  }, [templateForm.variabili_json]);
  const templateMissingInJson = useMemo(
    () =>
      templatePlaceholdersFromBody.filter(
        (placeholder) => !templateJsonKeys.includes(placeholder)
      ),
    [templateJsonKeys, templatePlaceholdersFromBody]
  );
  const templateEmailSolleciti = useMemo(
    () =>
      (templateSolleciti || []).filter(
        (item) => Number(item.attivo) === 1 && item.canale === 'email'
      ),
    [templateSolleciti]
  );
  const templateWhatsappSolleciti = useMemo(
    () =>
      (templateSolleciti || []).filter(
        (item) => Number(item.attivo) === 1 && item.canale === 'whatsapp'
      ),
    [templateSolleciti]
  );
  const templatePromemoriaSolleciti = useMemo(
    () => (promemoriaMode === 'reale_whatsapp' ? templateWhatsappSolleciti : templateEmailSolleciti),
    [promemoriaMode, templateEmailSolleciti, templateWhatsappSolleciti]
  );
  const scadenzaViewOptions = useMemo(() => {
    const today = new Date();
    const weekAhead = new Date(today);
    weekAhead.setDate(today.getDate() + 7);
    const firstDayMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const fmt = (date) =>
      new Intl.DateTimeFormat('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).format(date);
    const fmtIso = (iso) => {
      if (!iso) return 'da impostare';
      const parsed = new Date(`${iso}T00:00:00`);
      if (Number.isNaN(parsed.getTime())) return 'da impostare';
      return fmt(parsed);
    };
    return [
      { value: 'tutte', label: 'Tutte' },
      {
        value: 'scadute_fino_oggi',
        label: `Scadute fino a oggi (fino al ${fmt(today)})`,
      },
      {
        value: 'mese_corrente',
        label: `Mese corrente (dal ${fmt(firstDayMonth)} al ${fmt(lastDayMonth)})`,
      },
      {
        value: 'in_scadenza_settimana',
        label: `In scadenza 7 giorni (dal ${fmt(today)} al ${fmt(weekAhead)})`,
      },
      {
        value: 'intervallo_personalizzato',
        label: `Intervallo personalizzato (dal ${fmtIso(customFromDate)} al ${fmtIso(customToDate)})`,
      },
    ];
  }, [customFromDate, customToDate]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: '50%',
              bgcolor: '#ecfdf3',
              color: '#16a34a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CalendarDays size={20} />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Scadenzario • Rate Mensili
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Vista operativa scadenze, pagamenti e promemoria
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<BellRing size={16} />}
          onClick={handleSendPromemoria}
        >
          Invia promemoria
        </Button>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <StatWidget
            title="Importo previsto"
            value={`€ ${importoPrevistoTotale.toLocaleString('it-IT', { minimumFractionDigits: 2 })}`}
            icon={CalendarDays}
            color="#2563EB"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatWidget
            title="Importo pagato"
            value={`€ ${importoPagatoTotale.toLocaleString('it-IT', { minimumFractionDigits: 2 })}`}
            icon={HandCoins}
            color="#16A34A"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatWidget
            title="Saldo residuo"
            value={`€ ${saldoResiduoTotale.toLocaleString('it-IT', { minimumFractionDigits: 2 })}`}
            icon={Search}
            color="#CA8A04"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatWidget
            title="Rate scadute"
            value={rateScaduteCount}
            icon={BellRing}
            color="#DC2626"
          />
        </Grid>
      </Grid>

      <StyledCard>
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <TextField
              size="small"
              placeholder="Cerca ID rata / piano / cliente"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 280 }}
            />
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Visualizza</InputLabel>
              <Select
                label="Visualizza"
                value={scadenzaView}
                onChange={(e) => {
                  const nextView = e.target.value;
                  setScadenzaView(nextView);
                  if (nextView === 'intervallo_personalizzato') {
                    const today = new Date();
                    const todayIso = today.toISOString().slice(0, 10);
                    if (!customFromDate) setCustomFromDate(todayIso);
                    if (!customToDate) setCustomToDate(todayIso);
                  }
                  setPage(0);
                }}
              >
                {scadenzaViewOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {scadenzaView === 'intervallo_personalizzato' ? (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField
                  size="small"
                  type="date"
                  label="Scadono dal"
                  InputLabelProps={{ shrink: true }}
                  value={customFromDate}
                  onChange={(e) => {
                    setCustomFromDate(e.target.value);
                    setPage(0);
                  }}
                />
                <TextField
                  size="small"
                  type="date"
                  label="Scadono al"
                  InputLabelProps={{ shrink: true }}
                  value={customToDate}
                  onChange={(e) => {
                    setCustomToDate(e.target.value);
                    setPage(0);
                  }}
                />
              </Stack>
            ) : null}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Mostra/Nascondi filtri">
              <IconButton size="small" onClick={() => setShowFilters((prev) => !prev)}>
                <FilterAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Vista tabella">
              <IconButton size="small">
                <ViewColumnIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {showFilters && (
          <Box
            sx={{
              px: 3,
              py: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box sx={{ minWidth: 260 }}>
              <Autocomplete
                size="small"
                options={referentiWithAll}
                value={
                  referentiWithAll.find((item) => String(item.id) === String(userId)) ||
                  referentiWithAll[0]
                }
                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(option, value) =>
                  String(option.id) === String(value.id)
                }
                onChange={(_event, value) => {
                  setUserId(value ? String(value.id) : 'all');
                  setPage(0);
                }}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2">{option.name}</Typography>
                      {option.email ? (
                        <Typography variant="caption" color="text.secondary">
                          {option.email}
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>
                )}
                renderInput={(params) => <TextField {...params} label="Referente" />}
              />
            </Box>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Stato</InputLabel>
              <Select
                label="Stato"
                value={statoRate}
                onChange={(e) => {
                  setStatoRate(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="pendente,parzialmente_pagata,scaduta">
                  Aperte
                </MenuItem>
                <MenuItem value="pendente">Pendente</MenuItem>
                <MenuItem value="parzialmente_pagata">Parzialmente pagata</MenuItem>
                <MenuItem value="pagata">Pagata</MenuItem>
                <MenuItem value="scaduta">Scaduta</MenuItem>
                <MenuItem value="annullata">Annullata</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Promessa</InputLabel>
              <Select
                label="Promessa"
                value={promessaFilter}
                onChange={(e) => {
                  setPromessaFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="all">Tutte</MenuItem>
                <MenuItem value="with">Con promessa attiva</MenuItem>
                <MenuItem value="without">Senza promessa</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.neutral' }}>
                <TableCell padding="checkbox">
                  <Checkbox checked={allSelected} onChange={(e) => toggleSelectAll(e.target.checked)} />
                </TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell>{renderHeadCell('Piano', 'piano_rateazione_id')}</TableCell>
                <TableCell>{renderHeadCell('Rata', 'nr_rata')}</TableCell>
                <TableCell>{renderHeadCell('Scadenza', 'data_scadenza')}</TableCell>
                <TableCell>{renderHeadCell('Previsto', 'importo_previsto')}</TableCell>
                <TableCell>{renderHeadCell('Pagato', 'importo_pagato')}</TableCell>
                <TableCell>{renderHeadCell('Stato', 'stato_rate')}</TableCell>
                <TableCell>Promessa</TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedIds.includes(row.id)}
                      onChange={() => toggleSelect(row.id)}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 280 }}>
                    <Stack spacing={0.55}>
                      <Typography variant="body1" sx={{ fontWeight: 700, color: '#0F172A' }}>
                        {row.cliente?.full_name || `Cliente #${row.cliente_id}`}
                      </Typography>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.7, color: '#64748B' }}>
                        <Mail size={15} />
                        <Typography variant="body2" sx={{ color: 'inherit', lineHeight: 1.2 }}>
                          {row.cliente?.email || '-'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.7, color: '#64748B' }}>
                        <Phone size={15} />
                        <Typography variant="body2" sx={{ color: 'inherit', lineHeight: 1.2 }}>
                          {row.cliente?.telephone || '-'}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>#{row.piano_rateazione_id}</TableCell>
                  <TableCell>
                    {row.nr_rata}/{row.numero_rate_previste || '-'}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {row.data_scadenza
                        ? new Date(row.data_scadenza).toLocaleDateString('it-IT')
                        : '-'}
                    </Typography>
                    {(() => {
                      const visual = getRateVisualStatus(row);
                      const visualMeta = RATE_VISUAL_META[visual.key];
                      return (
                        <Typography
                          variant="caption"
                          sx={{ color: visualMeta.color, fontWeight: 700 }}
                        >
                          {getScadenzaLabel(row)}
                        </Typography>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    € {Number(row.importo_previsto || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell sx={{ color: Number(row.importo_pagato || 0) > 0 ? 'success.main' : 'text.primary' }}>
                    € {Number(row.importo_pagato || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const statoRateKey = String(row?.stato_rate || '').toLowerCase();
                      const stateMeta =
                        RATE_STATE_META[statoRateKey] || RATE_STATE_META.pendente;
                      return (
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 1.2,
                            py: 0.35,
                            borderRadius: 999,
                            bgcolor: stateMeta.bg,
                          }}
                        >
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              bgcolor: stateMeta.color,
                            }}
                          />
                          <Typography sx={{ fontWeight: 700, color: stateMeta.color }}>
                            {stateMeta.label}
                          </Typography>
                        </Box>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {row.promessa_attiva_id ? (
                      <Box
                        sx={{
                          display: 'inline-flex',
                          px: 1.1,
                          py: 0.35,
                          borderRadius: 999,
                          bgcolor: 'rgba(37, 99, 235, 0.12)',
                          color: '#2563EB',
                          fontWeight: 700,
                          fontSize: 12,
                        }}
                      >
                        Attiva
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Nessuna
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Worklist rata">
                      <IconButton
                        size="small"
                        onClick={() => openWorklistDialog(row)}
                        sx={{
                          color: '#2563EB',
                          bgcolor: 'rgba(37, 99, 235, 0.12)',
                        }}
                      >
                        <Eye size={16} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 5 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={30} />
                      <Typography variant="body2" color="text.secondary">
                        Caricamento rate in corso...
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    Nessuna rata trovata
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          rowsPerPageOptions={[10, 25, 50]}
          count={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </StyledCard>

      <Dialog
        open={worklistOpen}
        onClose={() => setWorklistOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Worklist rata {selectedRata ? `#${selectedRata.id}` : ''}
        </DialogTitle>
        <DialogContent>
          <Tabs
            value={worklistTab}
            onChange={(_e, value) => setWorklistTab(value)}
            sx={{ mb: 2 }}
          >
            <Tab label="Pagamenti" />
            <Tab label="Solleciti" />
            <Tab label="Promesse" />
            <Tab label="Storico" />
          </Tabs>

          {worklistLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              {worklistTab === 0 && (
                <Stack spacing={2}>
                  <Box
                    sx={{
                      p: 1.4,
                      borderRadius: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: summaryPulse
                        ? 'rgba(16, 185, 129, 0.12)'
                        : 'rgba(15, 23, 42, 0.02)',
                      boxShadow: summaryPulse
                        ? '0 0 0 2px rgba(16, 185, 129, 0.22) inset'
                        : 'none',
                      transform: summaryPulse ? 'translateY(-1px)' : 'translateY(0)',
                      transition:
                        'background-color 220ms ease, box-shadow 220ms ease, transform 220ms ease',
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      Scadenza rata:{' '}
                      {selectedRata?.data_scadenza
                        ? new Date(selectedRata.data_scadenza).toLocaleDateString('it-IT')
                        : '-'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Previsto €{' '}
                      {importoPrevistoSelezionato.toLocaleString('it-IT', {
                        minimumFractionDigits: 2,
                      })}{' '}
                      • Pagato €{' '}
                      {importoPagatoSelezionato.toLocaleString('it-IT', {
                        minimumFractionDigits: 2,
                      })}{' '}
                      • Residuo €{' '}
                      {importoResiduoSelezionato.toLocaleString('it-IT', {
                        minimumFractionDigits: 2,
                      })}
                    </Typography>
                  </Box>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                    <TextField
                      size="small"
                      label="Importo"
                      type="number"
                      value={newPagamentoForm.importo}
                      onChange={(e) =>
                        setNewPagamentoForm((prev) => ({ ...prev, importo: e.target.value }))
                      }
                    />
                    <TextField
                      size="small"
                      label="Data pagamento"
                      type="date"
                      helperText="Data in cui hai ricevuto il pagamento"
                      InputLabelProps={{ shrink: true }}
                      value={newPagamentoForm.data_pagamento}
                      onChange={(e) =>
                        setNewPagamentoForm((prev) => ({ ...prev, data_pagamento: e.target.value }))
                      }
                    />
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel>Metodo</InputLabel>
                      <Select
                        label="Metodo"
                        value={newPagamentoForm.metodo_pagamento}
                        onChange={(e) =>
                          setNewPagamentoForm((prev) => ({
                            ...prev,
                            metodo_pagamento: e.target.value,
                          }))
                        }
                      >
                        <MenuItem value="bonifico">Bonifico</MenuItem>
                        <MenuItem value="contanti">Contanti</MenuItem>
                        <MenuItem value="carta">Carta</MenuItem>
                        <MenuItem value="rid">RID</MenuItem>
                        <MenuItem value="assegno">Assegno</MenuItem>
                        <MenuItem value="altro">Altro</MenuItem>
                      </Select>
                    </FormControl>
                    <Tooltip title="Registra pagamento">
                      <IconButton
                        onClick={handleCreatePagamento}
                        sx={{
                          width: 40,
                          height: 40,
                          color: '#FFFFFF',
                          bgcolor: '#0F172A',
                          '&:hover': { bgcolor: '#111827' },
                        }}
                      >
                        <Check size={18} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Compila importo residuo">
                      <IconButton
                        onClick={() =>
                          setNewPagamentoForm((prev) => ({
                            ...prev,
                            importo: String(importoResiduoSelezionato || ''),
                          }))
                        }
                        sx={{
                          width: 40,
                          height: 40,
                          color: '#334155',
                          bgcolor: 'rgba(51, 65, 85, 0.1)',
                          '&:hover': { bgcolor: 'rgba(51, 65, 85, 0.18)' },
                        }}
                      >
                        <WandSparkles size={18} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <Divider />
                  {!!worklistPagamenti.length && (
                    <Box
                      sx={{
                        display: { xs: 'none', md: 'grid' },
                        gridTemplateColumns: '150px 130px 130px 120px 44px',
                        gap: 1,
                        px: 1,
                        color: 'text.secondary',
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      <Box>Importo</Box>
                      <Box>Data</Box>
                      <Box>Metodo</Box>
                      <Box>Stato</Box>
                      <Box />
                    </Box>
                  )}
                  {(worklistPagamenti || []).map((p) => (
                    <Box
                      key={p.id}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr auto', md: '150px 130px 130px 120px 44px' },
                        alignItems: 'center',
                        gap: 1,
                        px: 1,
                        py: 0.9,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1.5,
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                          €{' '}
                          {Number(p.importo || 0).toLocaleString('it-IT', {
                            minimumFractionDigits: 2,
                          })}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: { xs: 'block', md: 'none' } }}
                        >
                          {(p.data_pagamento
                            ? new Date(p.data_pagamento).toLocaleDateString('it-IT')
                            : '-') + ` • ${p.metodo_pagamento || '-'} • ${
                            p.annullato ? 'annullato' : 'valido'
                          }`}
                        </Typography>
                      </Box>
                      <Typography sx={{ display: { xs: 'none', md: 'block' }, fontSize: 14 }}>
                        {p.data_pagamento
                          ? new Date(p.data_pagamento).toLocaleDateString('it-IT')
                          : '-'}
                      </Typography>
                      <Typography sx={{ display: { xs: 'none', md: 'block' }, fontSize: 14 }}>
                        {p.metodo_pagamento || '-'}
                      </Typography>
                      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            px: 1,
                            py: 0.25,
                            borderRadius: 999,
                            bgcolor: p.annullato
                              ? 'rgba(148, 163, 184, 0.18)'
                              : 'rgba(16, 185, 129, 0.12)',
                            color: p.annullato ? '#64748B' : '#059669',
                            fontSize: 12,
                            fontWeight: 700,
                          }}
                        >
                          {p.annullato ? 'annullato' : 'valido'}
                        </Box>
                      </Box>
                      {!p.annullato ? (
                        <Tooltip title="Annulla pagamento">
                          <IconButton
                            size="small"
                            onClick={() => handleAnnullaPagamento(p.id)}
                            sx={{
                              color: '#EF4444',
                              bgcolor: 'rgba(239, 68, 68, 0.12)',
                              '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.2)' },
                            }}
                          >
                            <TicketX size={16} />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </Box>
                  ))}
                  {!worklistPagamenti.length && (
                    <Typography variant="body2" color="text.secondary">
                      Nessun pagamento registrato
                    </Typography>
                  )}
                </Stack>
              )}

              {worklistTab === 1 && (
                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                    <FormControl size="small" sx={{ minWidth: 240 }}>
                      <InputLabel>Template</InputLabel>
                      <Select
                        label="Template"
                        value={newSollecitoForm.template_id}
                        onChange={(e) => handleSelectTemplate(e.target.value)}
                      >
                        <MenuItem value="">Nessuno</MenuItem>
                        {filteredTemplateSolleciti.map((template) => (
                          <MenuItem key={template.id} value={String(template.id)}>
                            {template.nome}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel>Canale</InputLabel>
                      <Select
                        label="Canale"
                        value={newSollecitoForm.canale}
                        onChange={(e) =>
                          setNewSollecitoForm((prev) => ({ ...prev, canale: e.target.value }))
                        }
                      >
                        <MenuItem value="email">Email</MenuItem>
                        <MenuItem value="whatsapp">WhatsApp</MenuItem>
                        <MenuItem value="sms">SMS</MenuItem>
                        <MenuItem value="telefonata">Telefonata</MenuItem>
                        <MenuItem value="altro">Altro</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      size="small"
                      label="Tipo"
                      value={newSollecitoForm.tipo_sollecito}
                      onChange={(e) =>
                        setNewSollecitoForm((prev) => ({
                          ...prev,
                          tipo_sollecito: e.target.value,
                        }))
                      }
                    />
                    <Tooltip title="Nuovo template">
                      <IconButton
                        onClick={handleNewTemplate}
                        sx={{
                          width: 40,
                          height: 40,
                          color: '#2563EB',
                          bgcolor: 'rgba(37, 99, 235, 0.12)',
                          '&:hover': { bgcolor: 'rgba(37, 99, 235, 0.2)' },
                        }}
                      >
                        <FileText size={18} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Modifica template selezionato">
                      <span>
                        <IconButton
                          onClick={handleEditSelectedTemplate}
                          disabled={!newSollecitoForm.template_id}
                          sx={{
                            width: 40,
                            height: 40,
                            color: '#334155',
                            bgcolor: 'rgba(51, 65, 85, 0.12)',
                            '&:hover': { bgcolor: 'rgba(51, 65, 85, 0.2)' },
                          }}
                        >
                          <SquarePen size={16} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Registra sollecito">
                      <IconButton
                        onClick={handleCreateSollecito}
                        sx={{
                          width: 40,
                          height: 40,
                          color: '#FFFFFF',
                          bgcolor: '#0F172A',
                          '&:hover': { bgcolor: '#111827' },
                        }}
                      >
                        <Check size={18} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <TextField
                    size="small"
                    label="Messaggio"
                    multiline
                    minRows={2}
                    value={newSollecitoForm.messaggio_renderizzato}
                    onChange={(e) =>
                      setNewSollecitoForm((prev) => ({
                        ...prev,
                        messaggio_renderizzato: e.target.value,
                      }))
                    }
                  />
                  <Divider />
                  {(worklistSolleciti || []).map((s) => (
                    <Box key={s.id} sx={{ p: 1.2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {s.tipo_sollecito} • {s.canale}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {s.created_at
                          ? new Date(s.created_at).toLocaleString('it-IT')
                          : '-'} • {s.stato_esito}
                        {s.template_nome ? ` • template: ${s.template_nome}` : ''}
                      </Typography>
                    </Box>
                  ))}
                  {!worklistSolleciti.length && (
                    <Typography variant="body2" color="text.secondary">
                      Nessun sollecito registrato
                    </Typography>
                  )}
                </Stack>
              )}

              {worklistTab === 2 && (
                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                    <TextField
                      size="small"
                      label="Data promessa"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      value={newPromessaForm.data_promessa}
                      onChange={(e) =>
                        setNewPromessaForm((prev) => ({ ...prev, data_promessa: e.target.value }))
                      }
                    />
                    <TextField
                      size="small"
                      label="Importo promesso"
                      type="number"
                      value={newPromessaForm.importo_promesso}
                      onChange={(e) =>
                        setNewPromessaForm((prev) => ({
                          ...prev,
                          importo_promesso: e.target.value,
                        }))
                      }
                    />
                    <Tooltip title="Registra promessa">
                      <IconButton
                        onClick={handleCreatePromessa}
                        sx={{
                          width: 40,
                          height: 40,
                          color: '#FFFFFF',
                          bgcolor: '#0F172A',
                          '&:hover': { bgcolor: '#111827' },
                        }}
                      >
                        <Check size={18} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <TextField
                    size="small"
                    label="Note promessa"
                    multiline
                    minRows={2}
                    value={newPromessaForm.note}
                    onChange={(e) =>
                      setNewPromessaForm((prev) => ({ ...prev, note: e.target.value }))
                    }
                  />
                  <Divider />
                  {(worklistPromesse || []).map((p) => (
                    <Box key={p.id} sx={{ p: 1.2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          € {Number(p.importo_promesso || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                        </Typography>
                        {(() => {
                          const metaPromessa = getPromessaMeta(
                            promessaEdits[p.id]?.stato_promessa || p.stato_promessa
                          );
                          return (
                            <Box
                              sx={{
                                display: 'inline-flex',
                                px: 1.1,
                                py: 0.35,
                                borderRadius: 999,
                                bgcolor: metaPromessa.bg,
                                color: metaPromessa.color,
                                fontWeight: 700,
                                fontSize: 12,
                              }}
                            >
                              {metaPromessa.label}
                            </Box>
                          );
                        })()}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Data promessa: {p.data_promessa ? new Date(p.data_promessa).toLocaleDateString('it-IT') : '-'}
                      </Typography>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mt: 1 }}>
                        <FormControl size="small" sx={{ minWidth: 170 }}>
                          <InputLabel>Stato promessa</InputLabel>
                          <Select
                            label="Stato promessa"
                            value={promessaEdits[p.id]?.stato_promessa || p.stato_promessa || 'aperta'}
                            onChange={(e) =>
                              handlePromessaFieldChange(p.id, 'stato_promessa', e.target.value)
                            }
                          >
                            <MenuItem value="aperta">Aperta</MenuItem>
                            <MenuItem value="mantenuta">Mantenuta</MenuItem>
                            <MenuItem value="mancata">Mancata</MenuItem>
                            <MenuItem value="scaduta">Scaduta</MenuItem>
                            <MenuItem value="annullata">Annullata</MenuItem>
                          </Select>
                        </FormControl>
                        <TextField
                          size="small"
                          label="Note"
                          fullWidth
                          value={promessaEdits[p.id]?.note ?? p.note ?? ''}
                          onChange={(e) =>
                            handlePromessaFieldChange(p.id, 'note', e.target.value)
                          }
                        />
                        <Tooltip title="Salva promessa">
                          <IconButton
                            onClick={() => handleSavePromessa(p.id)}
                            sx={{
                              width: 40,
                              height: 40,
                              color: '#334155',
                              bgcolor: 'rgba(51, 65, 85, 0.1)',
                              '&:hover': { bgcolor: 'rgba(51, 65, 85, 0.18)' },
                            }}
                          >
                            <Save size={17} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Box>
                  ))}
                  {!worklistPromesse.length && (
                    <Typography variant="body2" color="text.secondary">
                      Nessuna promessa registrata
                    </Typography>
                  )}
                </Stack>
              )}

              {worklistTab === 3 && (
                <Stack spacing={1.5}>
                  {(worklistStorico || []).map((e, index) => {
                    const meta = getStoricoMeta(e.evento_tipo);
                    const Icon = meta.Icon;
                    const isLast = index === (worklistStorico || []).length - 1;
                    return (
                      <Box key={e.id} sx={{ display: 'flex', gap: 1.5, alignItems: 'stretch' }}>
                        <Box sx={{ width: 42, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <Box
                            sx={{
                              width: 34,
                              height: 34,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: meta.bg,
                              color: meta.color,
                              mt: 0.35,
                            }}
                          >
                            <Icon size={17} />
                          </Box>
                          {!isLast ? (
                            <Box
                              sx={{
                                flex: 1,
                                width: 2,
                                mt: 0.55,
                                bgcolor: 'rgba(148, 163, 184, 0.25)',
                                minHeight: 34,
                              }}
                            />
                          ) : null}
                        </Box>
                        <Box
                          sx={{
                            flex: 1,
                            p: 1.2,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1.5,
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 800, color: '#0F172A' }}>
                            {meta.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {e.created_at
                              ? new Date(e.created_at).toLocaleString('it-IT')
                              : '-'} • fonte: {e.source || '-'}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                  {!worklistStorico.length && (
                    <Typography variant="body2" color="text.secondary">
                      Nessun evento storico disponibile
                    </Typography>
                  )}
                </Stack>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWorklistOpen(false)}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{templateForm.id ? 'Modifica template sollecito' : 'Nuovo template sollecito'}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.2} sx={{ mt: 1 }}>
            <FormControl size="small">
              <InputLabel>Template esistente</InputLabel>
              <Select
                label="Template esistente"
                value={templateForm.id ? String(templateForm.id) : ''}
                onChange={(e) => handlePickTemplateToEdit(e.target.value)}
              >
                <MenuItem value="">Nuovo template</MenuItem>
                {(templateSolleciti || []).map((template) => (
                  <MenuItem key={template.id} value={String(template.id)}>
                    {template.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Codice"
              value={templateForm.codice}
              onChange={(e) =>
                setTemplateForm((prev) => ({ ...prev, codice: e.target.value }))
              }
            />
            <TextField
              size="small"
              label="Nome"
              value={templateForm.nome}
              onChange={(e) =>
                setTemplateForm((prev) => ({ ...prev, nome: e.target.value }))
              }
            />
            <FormControl size="small">
              <InputLabel>Canale</InputLabel>
              <Select
                label="Canale"
                value={templateForm.canale}
                onChange={(e) =>
                  setTemplateForm((prev) => ({ ...prev, canale: e.target.value }))
                }
              >
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="whatsapp">WhatsApp</MenuItem>
                <MenuItem value="sms">SMS</MenuItem>
                <MenuItem value="telefonata">Telefonata</MenuItem>
                <MenuItem value="altro">Altro</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Soggetto"
              value={templateForm.soggetto}
              onChange={(e) =>
                setTemplateForm((prev) => ({ ...prev, soggetto: e.target.value }))
              }
            />
            <TextField
              size="small"
              label="Corpo template"
              multiline
              minRows={4}
              value={templateForm.corpo_template}
              onChange={(e) =>
                setTemplateForm((prev) => ({ ...prev, corpo_template: e.target.value }))
              }
            />
            <TextField
              size="small"
              label="Variabili JSON"
              multiline
              minRows={3}
              value={templateForm.variabili_json}
              onChange={(e) =>
                setTemplateForm((prev) => ({ ...prev, variabili_json: e.target.value }))
              }
            />
            {templateJsonInvalid ? (
              <Typography variant="caption" sx={{ color: '#DC2626' }}>
                JSON non valido
              </Typography>
            ) : null}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.7 }}>
              {templatePlaceholdersFromBody.map((key) => (
                <Box
                  key={`body-${key}`}
                  sx={{
                    px: 1,
                    py: 0.35,
                    borderRadius: 999,
                    bgcolor: 'rgba(37, 99, 235, 0.12)',
                    color: '#1D4ED8',
                  }}
                >
                  <Typography variant="caption">{`{{${key}}}`}</Typography>
                </Box>
              ))}
              {!templatePlaceholdersFromBody.length ? (
                <Typography variant="caption" color="text.secondary">
                  Nessuna variabile nel corpo template
                </Typography>
              ) : null}
            </Box>
            {templateMissingInJson.length ? (
              <Typography variant="caption" sx={{ color: '#CA8A04' }}>
                Variabili usate nel corpo ma non dichiarate nel JSON: {templateMissingInJson.join(', ')}
              </Typography>
            ) : null}
            <FormControl size="small">
              <InputLabel>Stato</InputLabel>
              <Select
                label="Stato"
                value={templateForm.attivo ? '1' : '0'}
                onChange={(e) =>
                  setTemplateForm((prev) => ({
                    ...prev,
                    attivo: String(e.target.value) === '1',
                  }))
                }
              >
                <MenuItem value="1">Attivo</MenuItem>
                <MenuItem value="0">Disattivo</MenuItem>
              </Select>
            </FormControl>
            <Divider />
            <Typography variant="caption" color="text.secondary">
              Template disponibili: {templateLoading ? 'caricamento...' : templateSolleciti.length}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialogOpen(false)}>Chiudi</Button>
          <Button variant="contained" onClick={handleSaveTemplate}>
            Salva
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={promemoriaConfirmOpen}
        onClose={() => setPromemoriaConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Conferma avvio promemoria</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: '1px solid rgba(37, 99, 235, 0.2)',
                bgcolor: 'rgba(37, 99, 235, 0.06)',
                display: 'flex',
                alignItems: 'center',
                gap: 1.2,
              }}
            >
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: '50%',
                  bgcolor: 'rgba(37, 99, 235, 0.14)',
                  color: '#2563EB',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <BellRing size={16} />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Invio promemoria su rate selezionate
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Verranno elaborate {selectedIds.length} rate.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  bgcolor: 'rgba(245, 158, 11, 0.14)',
                  color: '#D97706',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <WandSparkles size={15} />
              </Box>
              <Typography variant="body2" color="text.secondary">
                Nel passaggio successivo puoi scegliere modalità, template e account mittente.
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  bgcolor: 'rgba(22, 163, 74, 0.14)',
                  color: '#16A34A',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Check size={15} />
              </Box>
              <Typography variant="body2" color="text.secondary">
                Confermando, apri la configurazione dell&apos;invio e potrai procedere solo dopo verifica finale.
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPromemoriaConfirmOpen(false)}>Annulla</Button>
          <Button variant="contained" onClick={handleProceedPromemoria} startIcon={<BellRing size={15} />}>
            Continua
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={promemoriaDialogOpen}
        onClose={() => {
          if (!promemoriaSending) setPromemoriaDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Invio promemoria</DialogTitle>
        <DialogContent>
          <Stack spacing={1.4} sx={{ mt: 1 }}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: '1px solid rgba(37, 99, 235, 0.2)',
                bgcolor: 'rgba(37, 99, 235, 0.06)',
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.6 }}>
                Cosa succede quando confermi
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Il sistema elabora le rate selezionate e registra l&apos;attività di promemoria.
                In base alla modalità scelta, può limitarsi alla registrazione oppure predisporre un invio reale.
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Rate selezionate: {selectedIds.length}
            </Typography>
            <FormControl size="small">
              <InputLabel>Modalità</InputLabel>
              <Select
                label="Modalità"
                value={promemoriaMode}
                onChange={(e) => {
                  const nextMode = e.target.value;
                  setPromemoriaMode(nextMode);
                  setPromemoriaTemplateId('');
                }}
              >
                <MenuItem value="simulato">Solo registra promemoria</MenuItem>
                <MenuItem value="reale_email">Invio email reale</MenuItem>
                <MenuItem value="reale_whatsapp">Predisponi WhatsApp</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary">
              {promemoriaMode === 'simulato'
                ? 'Modalità simulata: registra solo il promemoria senza invio verso canali esterni.'
                : promemoriaMode === 'reale_email'
                  ? "Modalità email reale: usa account e template selezionati per l'invio."
                  : 'Modalità WhatsApp: prepara i solleciti da inviare tramite integrazione esterna.'}
            </Typography>
            {promemoriaMode === 'reale_email' || promemoriaMode === 'reale_whatsapp' ? (
              <>
                {promemoriaMode === 'reale_email' ? (
                  <FormControl size="small">
                    <InputLabel>Account mittente</InputLabel>
                    <Select
                      label="Account mittente"
                      value={promemoriaAccountId}
                      onChange={(e) => setPromemoriaAccountId(e.target.value)}
                    >
                      {promemoriaEmailAccounts.map((account) => (
                        <MenuItem key={account.id} value={String(account.id)}>
                          {account.name || account.email} • {account.email}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : null}
                <FormControl size="small">
                  <InputLabel>
                    {promemoriaMode === 'reale_whatsapp' ? 'Template WhatsApp' : 'Template email'}
                  </InputLabel>
                  <Select
                    label={
                      promemoriaMode === 'reale_whatsapp'
                        ? 'Template WhatsApp'
                        : 'Template email'
                    }
                    value={promemoriaTemplateId}
                    onChange={(e) => setPromemoriaTemplateId(e.target.value)}
                  >
                    <MenuItem value="">Default automatico</MenuItem>
                    {templatePromemoriaSolleciti.map((template) => (
                      <MenuItem key={template.id} value={String(template.id)}>
                        {template.nome}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary">
                  {promemoriaMode === 'reale_email'
                    ? promemoriaEmailAccountsLoading
                      ? 'Caricamento account email...'
                      : `Account email attivi: ${promemoriaEmailAccounts.length}`
                    : 'WhatsApp predisposto: crea solleciti in stato da_inviare (integrazione invio esterno da collegare).'}
                </Typography>
              </>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
            Stai per elaborare {selectedIds.length} {selectedIds.length === 1 ? 'rata' : 'rate'} in modalità{' '}
            {promemoriaMode === 'simulato'
              ? 'Solo registra promemoria'
              : promemoriaMode === 'reale_email'
                ? 'Invio email reale'
                : 'Predisponi WhatsApp'}
            .
          </Typography>
          <Button onClick={() => setPromemoriaDialogOpen(false)} disabled={promemoriaSending}>
            Annulla
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmSendPromemoria}
            disabled={promemoriaSending}
          >
            {promemoriaSending ? 'Invio in corso...' : 'Conferma'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
